import { useId } from 'react';

import type { TokenSession } from '@/features/storefront/api/storefront';
import { useShopContent } from '@/features/storefront/content/useContent';
import {
  ANSWER_LIMITS,
  DAYS_SHOWN,
  FIELD_STYLE,
  calendarLocale,
  dayPeriod,
  formatTime,
  type StripDay,
} from '@/features/storefront/sections/bookingDays';
import { sessionKey } from '@/features/storefront/sections/bookingSlots';
import type { BookingQuestion, BookingsSection } from '@/features/storefront/sections/schema';
import { cn } from '@/lib/cn';

/**
 * The fields and the day strip both shapes of the bookings form draw.
 *
 * `BookingCalendar` (the one-page form every section has always drawn) and
 * `BookingWizard` (the same booking, a step at a time) ask the same questions
 * in the same fields, so those live here once - beside `bookingDays.ts`,
 * which holds the date arithmetic they share. Two copies would be two places
 * for a field to drift between shapes a merchant switches with one toggle.
 */

/** The fewest digits a booking's phone number has - the server's `MIN_PHONE_DIGITS`. */
const MIN_PHONE_DIGITS = 7;

/**
 * Whether `phone` can be a phone number: the business calls or messages it,
 * so a booking needs one. Digits and the usual separators only, as the
 * server checks it (`booking_phone` in `schemas/booking.py`).
 */
export function bookingPhoneOk(phone: string): boolean {
  const value = phone.trim();
  return /^[0-9+\-(). ]+$/.test(value) && (value.match(/\d/g)?.length ?? 0) >= MIN_PHONE_DIGITS;
}

export function Field({
  label,
  multiline = false,
  ...props
}: {
  label: string;
  multiline?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement> &
  React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  // The label is the placeholder, as in the contact form, so it is given to
  // assistive technology too - a field that announces itself as nothing is
  // unusable with a screen reader however it looks.
  const style = FIELD_STYLE;

  return multiline ? (
    <textarea
      {...props}
      rows={3}
      aria-label={label}
      placeholder={label}
      className="w-full border px-4 py-3 text-sm"
      style={style}
    />
  ) : (
    <input
      {...props}
      aria-label={label}
      placeholder={label}
      className="h-12 w-full border px-4 text-sm"
      style={style}
    />
  );
}


/**
 * "Send me updates about this appointment on WhatsApp" - drawn beneath the
 * phone field, unticked, in the same look as the merchant's own checkbox
 * questions. Inert until a number is typed: the opt-in is for that number,
 * and a tick with nothing to message is not a choice the shopper can make.
 */
export function WhatsAppOptIn({
  checked,
  disabled,
  onChange,
  className,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
}) {
  const copy = useShopContent();
  const id = useId();
  return (
    <div
      className={cn('flex items-start gap-3 text-sm', disabled && 'opacity-60', className)}
      data-booking-whatsapp
    >
      <input
        id={id}
        type="checkbox"
        checked={checked && !disabled}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        aria-describedby={`${id}-hint`}
        className={cn('mt-0.5 size-5 shrink-0', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}
        style={{ accentColor: 'var(--storefront-primary)' }}
      />
      <span>
        <label htmlFor={id} className={disabled ? undefined : 'cursor-pointer'}>
          {copy('booking.whatsapp_opt_in')}
        </label>
        <span id={`${id}-hint`} className="mt-0.5 block text-xs" style={{ color: 'var(--storefront-muted)' }}>
          {copy('booking.whatsapp_opt_in_hint')}
        </span>
      </span>
    </div>
  );
}

/**
 * One of the merchant's own questions, as a field.
 *
 * Two looks, matching the two forms it sits in: `placeholder` is the one-page
 * form's (the label inside the field, as its name and email are), `labelled`
 * the step-by-step form's, where every field carries a visible label above it.
 * Either way the question is the field's accessible name, and a required one
 * is `required` to the browser as well as marked for the eye.
 */
export function QuestionField({
  question,
  value,
  onChange,
  variant,
}: {
  question: BookingQuestion;
  value: string | boolean | undefined;
  onChange: (value: string | boolean) => void;
  variant: 'placeholder' | 'labelled';
}) {
  const copy = useShopContent();
  const id = useId();
  const text = typeof value === 'string' ? value : '';
  const label = question.label.trim();
  // A star the eye sees and a screen reader does not - `required` already
  // tells assistive technology, and "Allergies star" helps nobody. It sits
  // beside the label rather than in it, so the field's name is the question.
  const mark = question.required ? (
    <span aria-hidden style={{ color: 'var(--storefront-primary)' }}>
      {' *'}
    </span>
  ) : null;

  if (question.kind === 'checkbox') {
    return (
      <div className="flex items-start gap-3 text-sm" data-booking-question={question.id}>
        <input
          id={id}
          type="checkbox"
          required={question.required}
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
          className="mt-0.5 size-5 shrink-0 cursor-pointer"
          style={{ accentColor: 'var(--storefront-primary)' }}
        />
        <span>
          <label htmlFor={id} className="cursor-pointer">
            {label}
          </label>
          {mark}
        </span>
      </div>
    );
  }

  const heading =
    variant === 'labelled' ? (
      <div className="mb-1.5 text-sm font-medium">
        <label htmlFor={id}>{label}</label>
        {mark}
      </div>
    ) : null;
  const named =
    variant === 'placeholder'
      ? { 'aria-label': label, placeholder: question.required ? `${label} *` : label }
      : {};

  if (question.kind === 'select') {
    return (
      <div data-booking-question={question.id}>
        {heading}
        <select
          id={id}
          {...(variant === 'placeholder' ? { 'aria-label': label } : {})}
          required={question.required}
          value={text}
          onChange={(event) => onChange(event.target.value)}
          className="h-12 w-full border px-4 text-sm"
          style={FIELD_STYLE}
        >
          <option value="">
            {variant === 'placeholder' ? named.placeholder : copy('booking.select_placeholder')}
          </option>
          {question.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div data-booking-question={question.id}>
      {heading}
      {question.kind === 'textarea' ? (
        <textarea
          id={id}
          {...named}
          rows={3}
          required={question.required}
          maxLength={ANSWER_LIMITS.textarea}
          value={text}
          onChange={(event) => onChange(event.target.value)}
          className="w-full border px-4 py-3 text-sm"
          style={FIELD_STYLE}
        />
      ) : (
        <input
          id={id}
          {...named}
          required={question.required}
          maxLength={ANSWER_LIMITS.text}
          value={text}
          onChange={(event) => onChange(event.target.value)}
          className="h-12 w-full border px-4 text-sm"
          style={FIELD_STYLE}
        />
      )}
    </div>
  );
}

/**
 * `date_picker: "strip"` - a row of days to scroll through, each with its
 * weekday, date and month, and the days with no times greyed out and said so.
 *
 * A carousel rather than a calendar page: on a phone a thumb flicks along a
 * row far more easily than it hits a seven-column grid, and a clinic that
 * books a fortnight ahead has no use for a month view. Paged a fortnight at a
 * time, as the one-page form's own strip is, so a year-long diary is still
 * reachable without three hundred buttons.
 */
export function DayStrip({
  section,
  days,
  day,
  page,
  locale,
  onChoose,
  onPage,
  hasTimes = (entry) => entry.open,
  legend,
}: {
  section: BookingsSection;
  days: readonly StripDay[];
  day: string;
  page: number;
  locale: string;
  onChoose: (iso: string) => void;
  onPage: (page: number) => void;
  /** Whether a day has anything to book; a closed weekday by default. */
  hasTimes?: (entry: StripDay) => boolean;
  /** The heading over the row; omitted, "Choose a day". */
  legend?: string | null;
}) {
  const copy = useShopContent();
  const format = (options: Intl.DateTimeFormatOptions, date: Date) =>
    new Intl.DateTimeFormat(calendarLocale(locale), options).format(date);
  const paged = section.booking_window_days + 1 > DAYS_SHOWN;
  const arrow = 'grid size-9 shrink-0 place-items-center self-center border text-base disabled:opacity-30';
  const arrowStyle = {
    borderColor: 'var(--storefront-surface)',
    borderRadius: '999px',
  };

  return (
    <fieldset data-booking-strip>
      {legend === null ? null : (
        <legend className="text-sm font-semibold">{legend ?? copy('booking.choose_day')}</legend>
      )}
      <div className="mt-3 flex gap-2">
        {paged ? (
          <button
            type="button"
            aria-label={copy('booking.earlier')}
            disabled={page === 0}
            onClick={() => onPage(Math.max(0, page - DAYS_SHOWN))}
            className={arrow}
            style={arrowStyle}
          >
            <span aria-hidden>‹</span>
          </button>
        ) : null}
        <div className="flex min-w-0 flex-1 snap-x gap-2 overflow-x-auto pb-1">
          {days.map((entry) => {
            const open = hasTimes(entry);
            const pressed = entry.iso === day;
            return (
              <button
                key={entry.iso}
                type="button"
                disabled={!open}
                aria-pressed={pressed}
                onClick={() => onChoose(entry.iso)}
                className={cn(
                  'flex w-[4.25rem] shrink-0 snap-start flex-col items-center gap-0.5 border px-1 py-2.5 text-center disabled:cursor-not-allowed disabled:opacity-40',
                )}
                style={{
                  borderColor: pressed ? 'var(--storefront-primary)' : 'var(--storefront-surface)',
                  borderRadius: 'var(--storefront-radius)',
                  backgroundColor: pressed ? 'var(--storefront-primary)' : 'transparent',
                  color: pressed ? 'var(--storefront-background)' : 'var(--storefront-text)',
                }}
                data-strip-day={entry.iso}
              >
                <span className="text-[11px] uppercase tracking-wide opacity-75">
                  {format({ weekday: 'short' }, entry.date)}
                </span>
                <span className="text-lg font-semibold leading-tight">{entry.date.getDate()}</span>
                <span className="text-[11px] opacity-75">{format({ month: 'short' }, entry.date)}</span>
                {open ? null : <span className="sr-only">{copy('booking.no_times')}</span>}
              </button>
            );
          })}
        </div>
        {paged ? (
          <button
            type="button"
            aria-label={copy('booking.later')}
            disabled={page + DAYS_SHOWN > section.booking_window_days}
            onClick={() => onPage(page + DAYS_SHOWN)}
            className={arrow}
            style={arrowStyle}
          >
            <span aria-hidden>›</span>
          </button>
        ) : null}
      </div>
    </fieldset>
  );
}

/**
 * The sessions of a person who books by token (an OPD queue), as cards to
 * pick from: "Morning · 09:00 – 13:00 · Next token #34 · approx. 11:30 · 6
 * left". A full or finished session stays on the list, disabled and said so,
 * so the shopper sees the evening is still open rather than wondering where
 * the morning went. Shared by both shapes of the bookings form; drawn in
 * place of the time grid for a token person, and under the times (with a
 * heading of its own) when "any available" mixes the two.
 */
export function TokenSessionCards({
  sessions,
  chosen,
  onChoose,
  locale,
  zone,
  showPerson = false,
  compact = false,
}: {
  sessions: readonly TokenSession[];
  /** The `sessionKey` of the picked session, if any. */
  chosen: string | null;
  onChoose: (session: TokenSession) => void;
  locale: string;
  zone?: string;
  /** Name whose session it is - for "any available", where there are several people. */
  showPerson?: boolean;
  compact?: boolean;
}) {
  const copy = useShopContent();
  return (
    <div className={cn('mt-3 grid gap-2', !compact && 'sm:grid-cols-2')} data-token-sessions>
      {sessions.map((session) => {
        const key = sessionKey(session);
        const on = key === chosen;
        const full = !session.available && session.remaining === 0;
        const time = `${formatTime(session.starts_at, locale, zone)} – ${formatTime(session.ends_at, locale, zone)}`;
        const head = [copy(`booking.${dayPeriod(session.starts_at, zone)}`), time].join(' · ');
        const facts = session.available
          ? [
              copy('booking.next_token', { number: session.next_token }),
              copy('booking.approx', { time: formatTime(session.estimated_at, locale, zone) }),
              session.remaining !== null
                ? copy(session.remaining === 1 ? 'booking.left_one' : 'booking.left_many', {
                    count: session.remaining,
                  })
                : null,
            ].filter((fact): fact is string => Boolean(fact))
          : [copy(full ? 'booking.session_full' : 'booking.session_closed')];
        return (
          <button
            key={key}
            type="button"
            disabled={!session.available}
            aria-pressed={on}
            onClick={() => onChoose(session)}
            className={cn(
              'flex w-full flex-col gap-0.5 border px-4 py-3 text-start text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-45 motion-reduce:transition-none',
              on && 'font-semibold',
            )}
            style={{
              borderColor: on ? 'var(--storefront-primary)' : 'var(--storefront-surface)',
              borderRadius: 'var(--storefront-radius)',
              backgroundColor: on
                ? 'color-mix(in srgb, var(--storefront-primary) 8%, var(--storefront-background))'
                : 'var(--storefront-background)',
              boxShadow: on ? '0 0 0 1px var(--storefront-primary)' : undefined,
              color: 'var(--storefront-text)',
            }}
            data-token-session={key}
            data-session-full={full || undefined}
          >
            {showPerson ? <span className="block font-semibold">{session.team_member_name}</span> : null}
            <span className="block tabular-nums">{head}</span>
            <span
              className="block text-xs font-normal tabular-nums"
              style={{ color: on ? 'var(--storefront-primary)' : 'var(--storefront-muted)' }}
            >
              {facts.join(' · ')}
            </span>
          </button>
        );
      })}
    </div>
  );
}
